import React from 'react'
import { BotIcon } from 'lucide-react'

import { Stack, Typography, useTheme } from '@mui/material'

/**
 * VRG-427 (AC5) — marks a support request `ErrorBoundary` filed automatically,
 * so it never reads as something the submitter wrote. Shown wherever a request
 * card/header already shows `SupportTypeBadge` — the company list, the internal
 * queue, and both detail pages — so a reader gets the same signal regardless of
 * which surface they are looking at (§36: one component, not four hand-rolls).
 *
 * Deliberately its own neutral grey rather than one of `SUPPORT_REQUEST_TYPES`'
 * hues: this is an ORTHOGONAL axis to type/status (a bug report can be either
 * human- or system-filed), so it must not compete visually with the badges that
 * already carry colour meaning on the same row.
 */
interface SystemGeneratedBadgeProps {
  /** How many times this exact error has recurred. Omit or pass ≤1 to hide the count. */
  occurrenceCount?: number
}

export const SystemGeneratedBadge = ({ occurrenceCount }: SystemGeneratedBadgeProps) => {
  const theme = useTheme()

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        flexShrink: 0,
        width: 'fit-content',
        px: 2,
        borderRadius: 1.5,
        minHeight: 28,
        border: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.secondary,
      }}
    >
      <BotIcon size={14} strokeWidth={1.5} />
      <Typography sx={{ fontWeight: 500, lineHeight: 1, fontSize: 12 }}>
        {occurrenceCount && occurrenceCount > 1
          ? `Auto-reported · ${occurrenceCount} times`
          : 'Auto-reported'}
      </Typography>
    </Stack>
  )
}
